/**
 * Edge Host Monitoring Service (#670)
 *
 * API endpoints for the monitoring pages:
 * - /v1/observability/hosts — list all hosts with status
 * - /v1/observability/hosts/:id — get single host
 * - /v1/observability/hosts/:id/metrics — get host metrics
 * - /v1/observability/alerts/active — list active alerts
 * - /v1/observability/rca/:alertId — get LLM RCA for an alert (#669)
 *
 * Backend endpoints #661/#668/#669 are implemented. This service calls real
 * endpoints and lets pages render explicit unavailable states when endpoints
 * return 404/503.
 */

import { observabilityApiClient, ApiError } from '@/core/infra_api_client'
import { isActiveAlert } from './alert_helpers'
import type {
  MonitoringHost,
  ActiveAlert,
  RcaResponse,
  HostMetrics,
  AlertSeverity,
} from '@/core/infra_types'

// ============= Error Helpers =============

/**
 * Classify unavailable reads without inferring whether an endpoint is deployed.
 * Uses instanceof ApiError to properly narrow the error type.
 */
function isNotFoundOrUnavailable(error: unknown): boolean {
  if (error instanceof ApiError && (error.status === 404 || error.status === 503)) {
    return true
  }
  return error instanceof Error && error.message.includes('VITE_OBSERVABILITY_API_URL is required')
}

/**
 * Error thrown when a request cannot supply data.
 * HTTP status alone cannot establish deployment state.
 */
export class MonitoringUnavailableError extends Error {
  constructor(endpoint: string) {
    super(`Data unavailable: ${endpoint}`)
    this.name = 'MonitoringUnavailableError'
  }
}

/**
 * Shared retry predicate for TanStack Query.
 * Returns false for MonitoringUnavailableError to skip retry (prevents 4 doomed requests).
 */
export const retryMonitoringRequest = (failureCount: number, error: unknown): boolean =>
  failureCount < 2 && !(error instanceof MonitoringUnavailableError)

// ============= Hosts =============

type WireMonitoringHost = Omit<MonitoringHost, 'firingAlerts'> & {
  firingAlerts?: MonitoringHost['firingAlerts'] | null
}

/** Response shape from GET /v1/observability/hosts */
interface HostsResponse {
  hosts: WireMonitoringHost[] | null
  total: number
}

function normalizeHost(host: WireMonitoringHost): MonitoringHost {
  return {
    ...host,
    firingAlerts: host.firingAlerts ?? [],
  }
}

/**
 * GET /v1/observability/hosts
 *
 * Returns all hosts visible to the current user (org-scoped or platform-admin cross-org).
 * Throws MonitoringUnavailableError if the backend endpoint returns 404/503.
 */
export async function listHosts(): Promise<MonitoringHost[]> {
  // Dev-only mock override for local development when backend isn't available
  if (import.meta.env.DEV && import.meta.env['VITE_USE_MONITORING_MOCK'] === 'true') {
    const { getMockHosts } = await import('./mocks/monitoring')
    console.warn('[monitoring] Using mock hosts data - VITE_USE_MONITORING_MOCK is set')
    return getMockHosts()
  }

  try {
    const response = await observabilityApiClient.get<HostsResponse>('hosts')
    return (response.hosts ?? []).map(normalizeHost)
  } catch (error) {
    if (isNotFoundOrUnavailable(error)) {
      throw new MonitoringUnavailableError('/v1/observability/hosts')
    }
    throw error
  }
}

/**
 * GET /v1/observability/hosts/:id
 *
 * Returns a single host by ID, or null if not found.
 * Throws MonitoringUnavailableError if the backend endpoint returns 503 (service unavailable).
 */
export async function getHost(id: string): Promise<MonitoringHost | null> {
  // Dev-only mock override
  if (import.meta.env.DEV && import.meta.env['VITE_USE_MONITORING_MOCK'] === 'true') {
    const { getMockHosts } = await import('./mocks/monitoring')
    console.warn('[monitoring] Using mock host data - VITE_USE_MONITORING_MOCK is set')
    return getMockHosts().find(h => h.id === id) ?? null
  }

  try {
    const host = await observabilityApiClient.get<WireMonitoringHost>(`hosts/${id}`)
    return normalizeHost(host)
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null
    }
    if (error instanceof ApiError && error.status === 503) {
      throw new MonitoringUnavailableError(`/v1/observability/hosts/${id}`)
    }
    throw error
  }
}

/**
 * GET /v1/observability/hosts/:id/metrics
 *
 * Returns time-series metrics for a host.
 * Throws MonitoringUnavailableError if the backend endpoint returns 404/503.
 */
export async function getHostMetrics(hostId: string): Promise<HostMetrics> {
  // Dev-only mock override
  if (import.meta.env.DEV && import.meta.env['VITE_USE_MONITORING_MOCK'] === 'true') {
    const { getMockMetrics } = await import('./mocks/monitoring')
    console.warn('[monitoring] Using mock metrics data - VITE_USE_MONITORING_MOCK is set')
    return getMockMetrics()
  }

  try {
    return await observabilityApiClient.get<HostMetrics>(`hosts/${hostId}/metrics`)
  } catch (error) {
    if (isNotFoundOrUnavailable(error)) {
      throw new MonitoringUnavailableError(`/v1/observability/hosts/${hostId}/metrics`)
    }
    throw error
  }
}

// ============= Alerts =============

export interface ListAlertsParams {
  hostId?: string
  environmentId?: string
  severity?: AlertSeverity
  includeResolved?: boolean
}

/** Response shape from GET /v1/observability/alerts/active */
interface AlertsResponse {
  alerts: ActiveAlert[] | null
  total: number
}

/**
 * GET /v1/observability/alerts/active
 *
 * Returns all active alerts.
 * Throws MonitoringUnavailableError if the backend endpoint returns 404/503 so
 * the UI does not report "all systems operational" when alert state is unknown.
 */
export async function listActiveAlerts(params?: ListAlertsParams): Promise<ActiveAlert[]> {
  // Dev-only mock override
  if (import.meta.env.DEV && import.meta.env['VITE_USE_MONITORING_MOCK'] === 'true') {
    const { getMockAlerts } = await import('./mocks/monitoring')
    const all = getMockAlerts()
    return all.filter((a) => {
      if (params?.hostId && a.hostId !== params.hostId) return false
      if (params?.severity && a.severity !== params.severity) return false
      if (!params?.includeResolved && !isActiveAlert(a)) return false
      return true
    })
  }

  try {
    const searchParams: Record<string, string> = {}
    if (params?.hostId) searchParams['host_id'] = params.hostId
    if (params?.environmentId) searchParams['environment_id'] = params.environmentId
    if (params?.severity) searchParams['severity'] = params.severity
    if (params?.includeResolved) searchParams['include_resolved'] = 'true'

    const response = await observabilityApiClient.get<AlertsResponse>('alerts/active', searchParams)
    return response.alerts ?? []
  } catch (error) {
    if (isNotFoundOrUnavailable(error)) {
      throw new MonitoringUnavailableError('/v1/observability/alerts/active')
    }
    throw error
  }
}

/**
 * GET /v1/observability/alerts/:id
 */
export async function getAlert(alertId: string): Promise<ActiveAlert | null> {
  // Dev-only mock override, kept in sync with listActiveAlerts so mock alert
  // rows can deep-link into /alerts/:fingerprint without calling the backend.
  if (import.meta.env.DEV && import.meta.env['VITE_USE_MONITORING_MOCK'] === 'true') {
    const { getMockAlerts } = await import('./mocks/monitoring')
    return getMockAlerts().find(a => a.fingerprint === alertId) ?? null
  }

  try {
    return await observabilityApiClient.get<ActiveAlert>(`alerts/${alertId}`)
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null
    }
    if (isNotFoundOrUnavailable(error)) {
      throw new MonitoringUnavailableError(`/v1/observability/alerts/${alertId}`)
    }
    throw error
  }
}

// ============= Alert Actions =============

/**
 * Ack error thrown when the backend returns 503 (alertmanager not configured).
 * UI should surface this state honestly with disabled action + reason.
 */
export class MuteUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'MuteUnavailableError'
  }
}

/**
 * Operator statuses on an alert (agion/Agion#2357). The same statuses show on
 * the Slack incident message; a change here updates that message.
 * - ack: "I'm on it"
 * - mitigate: "Mitigated"
 * - close: "Resolved" by an operator (independent of Alertmanager's status)
 */
export type AlertStatusAction = 'ack' | 'mitigate' | 'close'

/**
 * Thrown when the backend refuses a status change with 409, for example
 * "I'm on it" on an alert an operator already closed. The message is the
 * backend's reason.
 */
export class AlertStatusConflictError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AlertStatusConflictError'
  }
}

function statusError(error: unknown, alertId: string, status: AlertStatusAction): unknown {
  if (error instanceof ApiError && error.status === 409) {
    const body = typeof error.data === 'object' && error.data !== null
      ? (error.data as { error?: string; message?: string })
      : {}
    return new AlertStatusConflictError(body.error ?? body.message ?? 'Status change refused')
  }
  if (isNotFoundOrUnavailable(error)) {
    return new MonitoringUnavailableError(`/v1/observability/alerts/${alertId}/${status}`)
  }
  return error
}

/**
 * POST /v1/observability/alerts/:id/{ack|mitigate|close}
 *
 * Sets an operator status. Returns the updated alert. Setting a status that
 * is already set is a no-op that returns the stored alert.
 */
export async function setAlertStatus(alertId: string, status: AlertStatusAction): Promise<ActiveAlert> {
  try {
    return await observabilityApiClient.post<ActiveAlert>(`alerts/${alertId}/${status}`)
  } catch (error) {
    throw statusError(error, alertId, status)
  }
}

/**
 * DELETE /v1/observability/alerts/:id/{ack|mitigate|close}
 *
 * Clears an operator status (DELETE close reopens the alert). Returns the
 * updated alert.
 */
export async function clearAlertStatus(alertId: string, status: AlertStatusAction): Promise<ActiveAlert> {
  try {
    return await observabilityApiClient.delete<ActiveAlert>(`alerts/${alertId}/${status}`)
  } catch (error) {
    throw statusError(error, alertId, status)
  }
}

export interface MuteAlertRequest {
  duration: '1h' | '4h' | '24h'
}

/**
 * POST /v1/observability/alerts/:id/mute
 *
 * Mutes an alert for the specified duration. Returns the updated alert.
 * Throws MuteUnavailableError when backend returns 503 (alertmanager not configured).
 */
export async function muteAlert(alertId: string, request: MuteAlertRequest): Promise<ActiveAlert> {
  try {
    return await observabilityApiClient.post<ActiveAlert>(`alerts/${alertId}/mute`, request)
  } catch (error) {
    if (error instanceof ApiError && error.status === 503) {
      const msg = typeof error.data === 'object' && error.data !== null
        ? (error.data as { message?: string }).message ?? 'Mute unavailable'
        : 'Mute unavailable'
      throw new MuteUnavailableError(msg)
    }
    if (isNotFoundOrUnavailable(error)) {
      throw new MonitoringUnavailableError(`/v1/observability/alerts/${alertId}/mute`)
    }
    throw error
  }
}

/**
 * DELETE /v1/observability/alerts/:id/mute
 *
 * Unmutes an alert. Returns the updated alert.
 * Throws MuteUnavailableError when backend returns 503.
 */
export async function unmuteAlert(alertId: string): Promise<ActiveAlert> {
  try {
    return await observabilityApiClient.delete<ActiveAlert>(`alerts/${alertId}/mute`)
  } catch (error) {
    if (error instanceof ApiError && error.status === 503) {
      const msg = typeof error.data === 'object' && error.data !== null
        ? (error.data as { message?: string }).message ?? 'Unmute unavailable'
        : 'Unmute unavailable'
      throw new MuteUnavailableError(msg)
    }
    if (isNotFoundOrUnavailable(error)) {
      throw new MonitoringUnavailableError(`/v1/observability/alerts/${alertId}/mute`)
    }
    throw error
  }
}

// ============= RCA (#669) =============

/**
 * GET /v1/observability/rca/:alertId
 *
 * Returns LLM-generated root cause analysis for an alert. The server returns
 * its 24 h cache on a hit and generates a new summary on a miss (host metrics
 * and log lines go to the org's default AI model), so call this only from an
 * explicit user action, never on page open.
 * Gracefully degrades when backend is unavailable or rate-limited.
 * Returns { status: 'unavailable' } for 404/503; rethrows other errors.
 *
 * @param alertId - The alert fingerprint (16 lowercase hex chars)
 * @param orgId - Optional org ID for cross-org lookups (platform_admin only).
 *                Required when viewing alerts from another organization (#660 audit P1 fix).
 */
export async function getRca(alertId: string, orgId?: string): Promise<RcaResponse> {
  try {
    const params: Record<string, string> = {}
    if (orgId) {
      params['org_id'] = orgId
    }
    // No automatic retries: every request that misses the server cache counts
    // toward the host's daily AI summary limit and may call the AI model.
    return await observabilityApiClient.get<RcaResponse>(`rca/${alertId}`, params, { retry: 0 })
  } catch (error) {
    // Per issue spec: 404/503/unavailable all render the same "RCA unavailable" placeholder
    if (isNotFoundOrUnavailable(error)) {
      return { status: 'unavailable' }
    }
    // Rethrow other errors (500, network, etc.) so the UI can distinguish them
    throw error
  }
}
